import { useState } from 'react';
import PropTypes from 'prop-types';
import { familyApi } from '../../services/api';
import './CreateFamilyForm.css';

const CreateFamilyForm = ({ onFamilyCreated, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Family name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await familyApi.createFamily({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined
      });
      
      onFamilyCreated(response.data.family);
    } catch (err) {
      console.error('Error creating family:', err);
      setError(err.response?.data?.error || 'Failed to create family');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-family-form">
      <div className="form-header">
        <h3>Create a family</h3>
        <p>Create a family to share your reading journey with others.</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">
            Family name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter family name"
            maxLength={100}
            required
            disabled={loading}
          />
          <small className="form-text">
            Choose a name that identifies your family or group.
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="description">
            Description (optional)
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Enter a description for your family"
            maxLength={500}
            rows={3}
            disabled={loading}
          />
          <small className="form-text">
            Briefly describe your family or reading group.
          </small>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !formData.name.trim()}
          >
            {loading ? 'Creating...' : 'Create a family'}
          </button>
        </div>
      </form>

      <div className="form-info">
        <h4>What happens next?</h4>
        <ul>
          <li>Your family will be created with a unique invite code</li>
          <li>You and all your readers will automatically be added to the family</li>
          <li>You can share the invite code with others to join your family</li>
          <li>Family members can log reading for any reader in the family</li>
        </ul>
      </div>
    </div>
  );
};

CreateFamilyForm.propTypes = {
  onFamilyCreated: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default CreateFamilyForm;
